import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  AssetsClipSuggestClipsCreateOkResponse,
  ClipAssetResponse,
  assetsClipSuggestClipsCreate,
  assetsSavedSearchGenerateAssetsCreate,
  assetsSavedSearchRelevantSearchSourcesV1Retrieve,
  assetsHybridSearchRelevantSearchSourcesV1Retrieve,
  assetsHybridSearchRefreshCreate,
  assetsClipCloneCreate
} from '@goldcast/api/content';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { useClipTitleRename } from './useClipTitleRename';
import { useAllVisibleClips } from './useAllVisibleClips';
import {
  appendAllClips,
  canGenerateClips,
  onDuplicateClipSuccess,
  prependAllClips,
  removeClip,
  setCanGenerateClips,
  setClipGeneratingCount
} from '@/stores/clip';
import useFreeTrialHook from '@/hooks/useFreeTrialHook';
import { currentUser } from '@/stores/user';
import { core } from '@/stores/core';
import { showErrorToast } from '@/libs/toast/toast';
import { addUnseenClipIds } from '@/stores/unseenClips';
import useAnalytics from '@/hooks/useAnalytics';
import { userPreferencesStore } from '@/stores/userPreferencesStore';
import useAssetRating from '@/hooks/useAssetRating';
import useSavedSearch from '@/hooks/useSavedSearch';
import { savedAISearch } from '@/stores/savedAISearch';
import useDialog from '@/components/organisms/useDialog';
import { useAppContext } from '@/context/AppContext/AppContext';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { GENERATE_DEFAULT_LIMIT } from '@/Pages/TranscriptPage/constants';
import { AI_SEARCH_ERROR_CODES } from '@/Pages/Sessions/RecentEvents/AiClips/constants';
import { NavigationEndpoint } from '@/Pages/SessionDetail/types';

export const useClipList = ({ setFirstClipsAsDefault = true }: { setFirstClipsAsDefault?: boolean }) => {
  const { eventId: routeEventId, broadcastId: routeBroadcastId } = useParams<{
    eventId: string;
    broadcastId: string;
  }>();
  const closePopover = useCallback(() => {
    EventBus.dispatch(CustomEvents.ClipListPopUp, { isOpen: false });
  }, []);

  const { renamingClip, onSaveNewTitle, closeRenamingClipDialog, setRenamingClip } = useClipTitleRename();

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const canGenerateClipsState = useSyncExternalStore(canGenerateClips.subscribe, canGenerateClips.getSnapshot);
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);

  const { allVisibleClips } = useAllVisibleClips();

  const [activeClip, setActiveClip] = useState<Clip | null>(null);

  const [loadingMoreClips, setLoadingMoreClips] = useState(false);
  const [relevantSourceList, setRelevantSourceList] = useState({
    contents: [],
    search_term: '',
    metadata: null
  });
  const [generatingMoreClip, setGeneratingMoreClip] = useState(false);
  const { isListCollapsed } = useSyncExternalStore(userPreferencesStore.subscribe, userPreferencesStore.getSnapshot);

  const { incrementClipsUsed } = useFreeTrialHook();
  const { isSavedSearchPage } = useSavedSearch();

  const {
    isOpen: isSourceSelectionDialogOpen,
    closeDialog: closeSourceSelectionDialog,
    openDialog: openSourceSelectionDialog
  } = useDialog();

  const { eventId, broadcastId } = useMemo(() => {
    if (!activeClip) {
      return {
        eventId: routeEventId,
        broadcastId: routeBroadcastId
      };
    }
    return {
      eventId: activeClip.content.project.id,
      broadcastId: activeClip.content.id
    };
  }, [activeClip, routeEventId, routeBroadcastId]);

  const { trackContentCreated, trackContentDuplicated } = useAnalytics();
  const { resetAllAssetRatings } = useAssetRating();
  const { logger } = useAppContext();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { mutate: duplicateClip } = useMutation(
    (clipId: string) =>
      assetsClipCloneCreate({
        id: clipId,
        queryParams: {}
      }),
    {
      onSuccess: (duplicatedClip, clipId) => {
        trackContentDuplicated({
          sourceAssetId: clipId,
          assetId: duplicatedClip.id,
          asset: duplicatedClip,
          assetType: 'CLIP',
          state: 'Duplicated'
        });

        onDuplicateClipSuccess(duplicatedClip).then(() => {
          if (!location.pathname.endsWith(NavigationEndpoint.clips)) {
            navigate(`/${eventId}/${broadcastId}/${NavigationEndpoint.clips}`);
          }
          setSearchParams({ activeClipId: duplicatedClip.id }, { replace: true });
        });
      },
      onError: (err: any, clipId) => {
        trackContentDuplicated({
          sourceAssetId: clipId,
          assetType: 'CLIP',
          state: 'Failed',
          failureReason: err.message
        });

        showErrorToast('Oops! Failed to duplicate clip. Please try again.');
        logger.error('Failed to duplicate clip', err);
      }
    }
  );

  useEffect(() => {
    return () => {
      resetAllAssetRatings();
    };
  }, [resetAllAssetRatings]);

  useEffect(() => {
    if (!allVisibleClips.length) {
      return;
    }

    const clipId = searchParams.get('activeClipId');
    const activeClip = allVisibleClips.find(clip => clip.id === clipId) ?? allVisibleClips[0];
    if (setFirstClipsAsDefault) {
      setActiveClip(activeClip ?? null);
      setSearchParams({ activeClipId: activeClip?.id }, { replace: true, state: location.state });
    }
  }, [searchParams, allVisibleClips, setFirstClipsAsDefault, setSearchParams, location.state]);

  const trackClipsGeneratedByAI = useCallback(
    (clips: AssetsClipSuggestClipsCreateOkResponse) => {
      trackContentCreated({
        count: clips.length,
        source: 'Autogenerate',
        state: 'Created'
      });
      clips.forEach(clip => {
        trackContentCreated({
          asset: clip,
          source: 'Autogenerate',
          state: 'Created'
        });
      });
    },
    [trackContentCreated]
  );

  const triggerGenerateClipsCall = useCallback(
    (selectedSource?: string[]) => {
      if (isSavedSearchPage) {
        const isNewAISearchEnabled = featureFlags[FeatureFlagKeys.Use_CL_New_AI_Search];

        if (isNewAISearchEnabled) {
          return assetsHybridSearchRefreshCreate({
            id: savedAISearch.getSnapshot()!.id,
            queryParams: {
              limit: 6
            },
            body: {
              generation_type: 'CLIP',
              selected_sources: selectedSource
            }
          });
        }

        return assetsSavedSearchGenerateAssetsCreate({
          id: savedAISearch.getSnapshot()!.id,
          queryParams: {
            limit: 5
          },
          body: {
            generation_type: 'CLIP',
            selected_sources: selectedSource
          }
        });
      }
      return assetsClipSuggestClipsCreate({
        body: {
          content_id: broadcastId as string,
          project_id: eventId as string,
          organization_id: currentUser.getSnapshot()?.organization as string,
          limit: GENERATE_DEFAULT_LIMIT
        },
        queryParams: {}
      });
    },
    [isSavedSearchPage, broadcastId, eventId, featureFlags]
  );

  const generateClips = useCallback(
    (selectedSource?: string[]): Promise<void> => {
      setClipGeneratingCount(GENERATE_DEFAULT_LIMIT);
      return triggerGenerateClipsCall(selectedSource)
        .then(clips => {
          setClipGeneratingCount(0);

          if (!clips.length) {
            setCanGenerateClips(false);
            return;
          }

          if (isSavedSearchPage) {
            appendAllClips(clips);
            setTimeout(() => {
              document
                .getElementById(clips[clips.length - 1].id)
                ?.scrollIntoView({ inline: 'end', behavior: 'smooth' });
            }, 500);
          } else {
            prependAllClips(clips);
          }
          addUnseenClipIds(clips.map((clip: ClipAssetResponse) => clip.id));
          searchParams.set('activeClipId', clips[0].id);
          trackClipsGeneratedByAI(clips);

          if (!coreStore.content?.is_sample_upload) {
            incrementClipsUsed(clips.length);
          }
        })
        .catch(e => {
          setClipGeneratingCount(0);
          setCanGenerateClips(true);
          showErrorToast('Generation of clips stalled. Ensure your selections are clear and try again.');
          trackContentCreated({
            source: 'Autogenerate',
            state: 'Failed',
            failureReason: e?.mesage
          });
        });
    },
    [
      isSavedSearchPage,
      coreStore,
      incrementClipsUsed,
      trackContentCreated,
      searchParams,
      trackClipsGeneratedByAI,
      triggerGenerateClipsCall
    ]
  );

  const handleTranscriptError = (err: any) => {
    navigate(`/error/${err?.statusCode}`, { replace: true });
  };

  const handleOnDelete = useCallback(
    (clipId: string) => {
      const clipIndex = allVisibleClips.findIndex(clip => clip.id === clipId);
      removeClip(clipId).then(() => {
        if (allVisibleClips[clipIndex - 1]) {
          setSearchParams({ activeClipId: allVisibleClips[clipIndex - 1].id }, { replace: true });
        } else if (allVisibleClips[clipIndex + 1]) {
          setSearchParams({ activeClipId: allVisibleClips[clipIndex + 1].id }, { replace: true });
        } else {
          setSearchParams(undefined, { replace: true });
        }
      });
    },
    [allVisibleClips, setSearchParams]
  );

  const onGenerateSocialPost = (clipId: string) => {
    isSavedSearchPage
      ? navigate(`/search/${savedAISearch.getSnapshot()!.id}/social`, { state: { clipId } })
      : navigate(`/${eventId}/${broadcastId}/social`, { state: { clipId } });
    closePopover();
  };

  const [isClipsListHidden, setIsClipsListHidden] = useState(false);

  const onGenerateMoreClips = useCallback(
    (isLoading?: Boolean) => {
      if (isSavedSearchPage) {
        if (isLoading) {
          openSourceSelectionDialog();
          setLoadingMoreClips(true);
        }

        const isNewAISearchEnabled = featureFlags[FeatureFlagKeys.Use_CL_New_AI_Search];

        const relevantSearchSources = isNewAISearchEnabled
          ? assetsHybridSearchRelevantSearchSourcesV1Retrieve({
              queryParams: {
                saved_search: savedAISearch.getSnapshot()!.id,
                organization: currentUser.getSnapshot()?.organization as string,
                generation_type: 'CLIP'
              }
            })
          : assetsSavedSearchRelevantSearchSourcesV1Retrieve({
              queryParams: {
                saved_search: savedAISearch.getSnapshot()!.id,
                organization: currentUser.getSnapshot()?.organization as string,
                generation_type: 'CLIP'
              }
            });

        return relevantSearchSources
          .then((sourceList: any) => {
            setRelevantSourceList(sourceList);
            openSourceSelectionDialog();
          })
          .catch(err => {
            if (err && err.toString() === AI_SEARCH_ERROR_CODES.RELEVANT_SOURCES_NOT_FOUND) {
              showErrorToast('No relevant sources found.');
            }
            closeSourceSelectionDialog();
          })
          .finally(() => {
            setLoadingMoreClips(false);
          });
      }
      return generateClips();
    },
    [isSavedSearchPage, featureFlags, openSourceSelectionDialog, closeSourceSelectionDialog, generateClips]
  );

  const handleSelectedSource = useCallback(
    (selectedSource: string[]) => {
      setGeneratingMoreClip(true);
      generateClips(selectedSource).finally(() => {
        closeSourceSelectionDialog();
        setGeneratingMoreClip(false);
      });
    },
    [closeSourceSelectionDialog, generateClips]
  );

  return {
    isListCollapsed,
    allVisibleClips,
    canGenerateClipsState,
    isSourceSelectionDialogOpen,
    closeSourceSelectionDialog,
    duplicateClip,
    handleTranscriptError,
    handleOnDelete,
    onSaveNewTitle,
    isClipsListHidden,
    setRenamingClip,
    onGenerateMoreClips,
    onGenerateSocialPost,
    activeClip,
    isSavedSearchPage,
    broadcastId,
    eventId,
    renamingClip,
    closeRenamingClipDialog,
    loadingMoreClips,
    relevantSourceList,
    generatingMoreClip,
    handleSelectedSource,
    coreStore,
    setIsClipsListHidden
  };
};
